import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Gift, MessageSquare, Phone, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { ChannelIcon } from "@/components/ChannelIcon";
import { PAYMENT_HELP_URL } from "@/components/MetaServiceBillingBanner";
import { useBrand } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBillingOverview } from "@/hooks/useBillingOverview";
import { usePermissions } from "@/hooks/usePermissions";
import { useCreditCoverage } from "@/hooks/useCreditCoverage";
import { isRecargasPlan, recargasCanAnswer } from "@/lib/credits-plan";
import { useRecargasSummary } from "@/hooks/useRecargasSummary";
import { useOrgCompensations } from "@/hooks/useOrgCompensations";
import { availableBalance, displayStatus, type OrgCompensations } from "@/lib/compensation-usage";
import type { BillingOverview } from "@/lib/billing-overview";
import { formatCount } from "@/lib/conversation-count";
import { cn } from "@/lib/utils";
import { useVoiceCredits } from "@/hooks/useVoiceCredits";
import { formatCop as formatCopCredits } from "@/lib/credits";
import { voiceMinutesFor } from "@/lib/voice-credits";
import { canTopUp, PAYER_ROLE_REQUIRED_MESSAGE } from "@/lib/billing-payer";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";

export interface FreeRepliesRow {
  channelId: string;
  channelName: string;
  phone: string | null;
  delivered: number;
  allowance: number;
  projected: number;
  daysElapsed: number;
}

export const SERVICE_USD = 0.0008;
export const SERVICE_COP = 2.9455;

type ServiceCostRow = Pick<FreeRepliesRow, "delivered" | "allowance" | "projected">;

export function estimateServiceCost<T extends ServiceCostRow>(rows: T[]) {
  const perNumber = rows.map((r) => {
    const billable = Math.max(0, r.projected - r.allowance);
    const accruedBillable = Math.max(0, r.delivered - r.allowance);
    return {
      ...r,
      billable,
      usd: billable * SERVICE_USD,
      cop: billable * SERVICE_COP,
      accruedBillable,
      accruedUsd: accruedBillable * SERVICE_USD,
      accruedCop: accruedBillable * SERVICE_COP,
    };
  });
  const sum = (pick: (r: (typeof perNumber)[number]) => number) => perNumber.reduce((a, r) => a + pick(r), 0);
  return {
    perNumber,
    projected: sum((r) => r.projected),
    billable: sum((r) => r.billable),
    usd: sum((r) => r.usd),
    cop: sum((r) => r.cop),
    delivered: sum((r) => r.delivered),
    accruedBillable: sum((r) => r.accruedBillable),
    accruedUsd: sum((r) => r.accruedUsd),
    accruedCop: sum((r) => r.accruedCop),
  };
}

export const formatUsd = (n: number) =>
  `US$${n.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const formatCop = (n: number) => `$${formatCount(Math.round(n))}`;
export const formatMetaCost = (cop: number, usd: number) => `${formatCop(cop)} (${formatUsd(usd)})`;

interface VoiceMinutesBalance {
  purchased: number;
  adjusted: number;
  used: number;
  remaining: number;
  topups: number;
}

type Tone = "ok" | "warn" | "over";

const fmt = (n: number) => formatCount(Math.round(n));
const fmtMinutes = (n: number) => `${Math.floor(n).toLocaleString("es-CO")} min`;

const TONE_TEXT: Record<Tone, string> = { ok: "text-ink-900", warn: "text-amber-600", over: "text-destructive" };
const TONE_BAR: Record<Tone, string> = { ok: "bg-gradient-to-r from-flow-violet to-flow-blue-light", warn: "bg-amber-500", over: "bg-destructive" };

export interface PlanUsage {
  planName: string;
  perUse: boolean;
  lifetime: boolean;
  used: number;
  included: number | null;
  unlimited: boolean;
  tone: Tone;
  periodStart: string | null;
  periodEnd: string | null;
  quotaRenewsOn: string | null;
}

export function planUsage(overview: BillingOverview): PlanUsage {
  const rawName = overview.plan?.name?.trim() || "Gratuito";
  const planName = /^plan\b/i.test(rawName) ? rawName : `Plan ${rawName}`;
  const perUse = overview.plan?.billingMode === "per_use";
  const used = Math.max(0, overview.usage.conversations ?? overview.usage.cachedCounter);
  const noCap = overview.limit.effective <= 0 && !overview.limit.freeQuotaWithheld && !isRecargasPlan(overview.plan?.id);
  const included = perUse || overview.limit.unlimited || noCap ? null : overview.limit.effective;
  const tone: Tone = included === null ? "ok" : used >= included ? "over" : used >= included * 0.8 ? "warn" : "ok";
  return {
    planName,
    perUse,
    lifetime: overview.usage.scope === "lifetime",
    used,
    included,
    unlimited: !perUse && overview.limit.unlimited,
    tone,
    periodStart: overview.activePeriod?.start ?? null,
    periodEnd: overview.activePeriod?.endInclusive ?? null,
    quotaRenewsOn: overview.quotaWindow?.renewsOn ?? null,
  };
}

const fmtDay = (day: string) => format(new Date(`${day}T00:00:00`), "d MMM yyyy", { locale: es });

export interface OutboundRow {
  channelId: string;
  channelName: string;
  phone: string | null;
  sentTotal: number;
  sentReplies: number;
  sentTemplates: number;
  delivered: number;
  allowance: number;
  projected: number;
}

export function useOutboundUsage(orgId: string | null, enabled: boolean) {
  const [rows, setRows] = useState<OutboundRow[] | null>(null);
  useEffect(() => {
    if (!orgId || !enabled) {
      setRows(null);
      return;
    }
    let mounted = true;
    const load = async () => {
      const { data, error } = await supabase.rpc("whatsapp_outbound_month" as never, { p_org: orgId } as never);
      if (!mounted) return;
      const list = (error ? [] : (data as unknown as Record<string, unknown>[]) ?? []).map((r) => ({
        channelId: String(r.channel_id),
        channelName: String(r.channel_name ?? ""),
        phone: r.phone_number ? String(r.phone_number) : null,
        sentTotal: Number(r.sent_total) || 0,
        sentReplies: Number(r.sent_replies) || 0,
        sentTemplates: Number(r.sent_templates) || 0,
        delivered: Number(r.delivered_replies) || 0,
        allowance: Number(r.free_allowance) || 1000,
        projected: Number(r.projected_replies) || 0,
      }));
      setRows(list.length > 0 ? list : null);
    };
    void load();
    const interval = setInterval(load, 5 * 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, [orgId, enabled]);
  return rows;
}

export function outboundBar(total: number, allowance: number) {
  const scale = Math.max(total, allowance, 1);
  return { fillPct: (total / scale) * 100, markPct: total > allowance ? (allowance / scale) * 100 : null };
}

export interface SocialOutbound {
  instagram: number | null;
  messenger: number | null;
}

export function useSocialOutbound(orgId: string | null, enabled: boolean) {
  const [usage, setUsage] = useState<SocialOutbound | null>(null);
  useEffect(() => {
    if (!orgId || !enabled) {
      setUsage(null);
      return;
    }
    let mounted = true;
    const load = async () => {
      const { data, error } = await supabase.rpc("social_outbound_month" as never, { p_org: orgId } as never);
      if (!mounted) return;
      const rows = error ? [] : (data as unknown as Record<string, unknown>[]) ?? [];
      const count = (type: string) => {
        const row = rows.find((r) => r.channel_type === type);
        return row ? Number(row.sent_total) || 0 : null;
      };
      const next = { instagram: count("instagram"), messenger: count("facebook") };
      setUsage(next.instagram === null && next.messenger === null ? null : next);
    };
    void load();
    const interval = setInterval(load, 5 * 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, [orgId, enabled]);
  return usage;
}

export function overFreeReplies(rows: Pick<FreeRepliesRow, "delivered" | "allowance">[]) {
  return rows.reduce((sum, r) => sum + Math.max(0, r.delivered - r.allowance), 0);
}

export function freeRepliesLeft(rows: Pick<FreeRepliesRow, "delivered" | "allowance">[]) {
  return rows.reduce((sum, r) => sum + Math.max(0, r.allowance - r.delivered), 0);
}

function useVoiceMinutes(orgId: string | null, enabled: boolean) {
  const [balance, setBalance] = useState<VoiceMinutesBalance | null>(null);
  useEffect(() => {
    if (!orgId || !enabled) {
      setBalance(null);
      return;
    }
    let mounted = true;
    const load = async () => {
      const { data: account } = await supabase
        .from("voice_assistants_retell_accounts")
        .select("id")
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .eq("sync_status", "synced")
        .limit(1)
        .maybeSingle();
      if (!mounted) return;
      if (!account) {
        setBalance(null);
        return;
      }
      const { data, error } = await supabase.rpc("voice_minutes_balance" as never, { p_org: orgId } as never);
      if (!mounted) return;
      const rows = data as unknown as VoiceMinutesBalance[] | VoiceMinutesBalance | null;
      const row = Array.isArray(rows) ? rows[0] : rows;
      setBalance(error || !row ? null : {
        purchased: Number(row.purchased) || 0,
        adjusted: Number(row.adjusted) || 0,
        used: Number(row.used) || 0,
        remaining: Number(row.remaining) || 0,
        topups: Number(row.topups) || 0,
      });
    };
    void load();
    const interval = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(interval); };
  }, [orgId, enabled]);
  return balance;
}

function MiniBar({ pct, tone }: { pct: number; tone: Tone }) {
  return (
    <div className="h-1.5 w-12 shrink-0 overflow-hidden rounded-full bg-muted">
      <div className={cn("h-full rounded-full", TONE_BAR[tone])} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

function RepliesBar({ total, allowance, showMark = true }: { total: number; allowance: number; showMark?: boolean }) {
  const { fillPct, markPct } = outboundBar(total, allowance);
  return (
    <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-muted" title={`${fmt(allowance)} respuestas gratis`}>
      <div
        data-testid="whatsapp-replies-bar"
        className="h-full rounded-full bg-gradient-to-r from-flow-violet to-flow-blue-light"
        style={{ width: `${Math.max(0, Math.min(100, fillPct))}%` }}
      />
      {showMark && markPct !== null && (
        <div
          data-testid="whatsapp-free-mark"
          aria-hidden="true"
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-background"
          style={{ left: `${markPct}%` }}
        />
      )}
    </div>
  );
}

function OverChip({ children }: { children: ReactNode }) {
  return (
    <span className="shrink-0 rounded-full bg-flow-violet/10 px-1.5 py-px text-[10px] font-semibold tabular-nums text-flow-violet">
      {children}
    </span>
  );
}

function PaymentHelpLink() {
  return (
    <a href={PAYMENT_HELP_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-flow-violet hover:underline">
      Revisa tu método de pago
    </a>
  );
}

export interface UsageCardProps {
  showWhatsApp: boolean;
  showVoice: boolean;
  showPlan?: boolean;
  allowPlanChange?: boolean;
  showCompensations?: boolean;
}

export interface CompensationSummary {
  balance: number;
  live: number;
  used: number;
}

export function compensationSummary(data: OrgCompensations | null, now: Date = new Date()): CompensationSummary | null {
  if (!data || data.compensations.length === 0) return null;
  return {
    balance: availableBalance(data, now),
    live: data.compensations.filter((c) => displayStatus(c, now) === "credited").length,
    used: data.compensations.reduce((sum, c) => sum + c.used, 0),
  };
}


function WhatsAppEstimate({ cost }: { cost: ReturnType<typeof estimateServiceCost> }) {
  const free = cost.billable <= 0;
  return (
    <div data-testid="whatsapp-estimate" className="rounded-lg border border-flow-violet/20 bg-gradient-to-br from-flow-violet/5 to-flow-blue-light/10 px-3 py-2.5">
      <div className="text-[12px] text-mute">WhatsApp · estimado al cierre</div>
      <div className="flex items-baseline gap-1.5 tabular-nums">
        <span className="text-[22px] font-semibold leading-tight text-flow-violet">{free ? formatUsd(0) : `~${formatUsd(cost.usd)}`}</span>
        <span className="text-[12px] text-mute">{free ? formatCop(0) : `≈ ${formatCop(cost.cop)}`}</span>
      </div>
      <div className="text-[12px] text-mute tabular-nums">
        {free ? (
          "Dentro de las 1.000 respuestas gratis"
        ) : (
          <>
            Acumulado hoy <span className="font-semibold text-ink-900">{formatUsd(cost.accruedUsd)}</span> · {formatCop(cost.accruedCop)}
          </>
        )}
      </div>
    </div>
  );
}

function DetailBlock({ title, aside, testId, className, children }: { title: string; aside?: ReactNode; testId: string; className?: string; children: ReactNode }) {
  return (
    <section data-testid={testId} aria-label={title} className={cn("min-w-0 space-y-2.5 rounded-xl border p-4 text-[13px]", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function DetailRow({ label, children, valueClassName }: { label: ReactNode; children: ReactNode; valueClassName?: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-mute">{label}</span>
      <span className={cn("min-w-0 text-right font-medium tabular-nums text-ink-900", valueClassName)}>{children}</span>
    </div>
  );
}

function BlockLink({ onClick, children, disabled = false, title }: { onClick: () => void; children: ReactNode; disabled?: boolean; title?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className="shrink-0 text-[12px] font-medium text-flow-violet hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">
      {children} <span aria-hidden="true">›</span>
    </button>
  );
}

function CompactRow({ icon, label, value, valueClassName, testId, children }: { icon: ReactNode; label: string; value: ReactNode; valueClassName?: string; testId?: string; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      {icon}
      <span className="flex-1 truncate text-mute">{label}</span>
      <span className={cn("font-semibold tabular-nums text-ink-900", valueClassName)}>{value}</span>
      {children}
    </div>
  );
}

const ROW_ICON = "h-3.5 w-3.5 shrink-0 text-mute";

export function UsageCard({ showWhatsApp, showVoice, showPlan = false, allowPlanChange = true, showCompensations = false }: UsageCardProps) {
  const { activeWorkspaceId, userRole } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const userCanTopUp = canTopUp(userRole, isSuperAdmin);
  const topupTitle = userCanTopUp ? undefined : PAYER_ROLE_REQUIRED_MESSAGE;
  const navigate = useNavigate();
  const brand = useBrand();
  const { can, isLoading: permsLoading, isError: permsError } = usePermissions();
  const replies = useOutboundUsage(activeWorkspaceId, showWhatsApp);
  const social = useSocialOutbound(activeWorkspaceId, showWhatsApp);
  const voice = useVoiceMinutes(activeWorkspaceId, showVoice);
  const voiceTopupAllowed = brand.id !== "etb" && brand.voiceAssistants;
  const { summary: voiceWallet } = useVoiceCredits(activeWorkspaceId, showVoice && voiceTopupAllowed);
  const { overview, refresh: refreshOverview } = useBillingOverview(showPlan ? activeWorkspaceId : null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showSent, setShowSent] = useState(false);
  const { data: compensationData } = useOrgCompensations(activeWorkspaceId, showPlan && showCompensations);
  const compensation = compensationSummary(compensationData);

  useEffect(() => {
    if (!showPlan || !activeWorkspaceId) return;
    const interval = setInterval(refreshOverview, 5 * 60_000);
    return () => clearInterval(interval);
  }, [showPlan, activeWorkspaceId, refreshOverview]);

  const basePlan = showPlan && overview ? planUsage(overview) : null;
  const onRecargas = showPlan && !!overview && isRecargasPlan(overview.plan?.id);
  const { summary: recargas } = useRecargasSummary(activeWorkspaceId, onRecargas);
  const pastIncluded = !!basePlan && basePlan.included !== null && basePlan.used >= basePlan.included;
  const { coverage: creditCoverage } = useCreditCoverage(activeWorkspaceId, pastIncluded, basePlan?.used);
  const creditsPay = pastIncluded && creditCoverage?.covers === true;
  const plan = basePlan && creditsPay ? { ...basePlan, tone: "ok" as Tone } : basePlan;
  const canChangePlan = !permsLoading && !permsError && can("settings") && can("settings.account");

  if (!replies && !voice && !plan && !social) return null;

  const metaCost = replies ? estimateServiceCost(replies) : null;
  const overFree = replies ? overFreeReplies(replies) : 0;
  const freeLeft = replies ? freeRepliesLeft(replies) : 0;
  const totalAllowance = replies ? replies.reduce((sum, r) => sum + r.allowance, 0) : 0;
  const singleNumber = replies?.length === 1;
  const numberCount = replies?.length ?? 0;
  const numbersOverFree = replies ? replies.filter((r) => r.delivered > r.allowance).length : 0;
  const platformName = brand.id === "flow" ? "Flow" : "la plataforma";

  const vHasBalance = !!voice && voice.topups > 0;
  const vRemaining = voice ? Math.max(0, voice.remaining) : 0;
  const vLoaded = voice ? voice.purchased + voice.adjusted : 0;
  const vShare = vLoaded > 0 ? vRemaining / vLoaded : 0;
  const vTone: Tone = !vHasBalance || vRemaining <= 0 ? "over" : vShare < 0.1 ? "warn" : "ok";
  const vWallet = voice && voiceWallet?.enrolled && !voiceWallet.exempt ? voiceWallet : null;
  const vExempt = !!voiceWallet?.exempt;
  const vLegacy = !!voice && !vWallet && !vExempt;
  const vWalletTone: Tone = vWallet && !vWallet.canCall ? "over" : "ok";
  const vWalletMinutes = vWallet ? voiceMinutesFor(vWallet.balanceCop, vWallet.priceCop) : null;
  const vCanTopUp = !!voice && voiceTopupAllowed && !!voiceWallet?.priceCop && !vExempt;
  const goTo = (path: string) => {
    setDetailOpen(false);
    navigate(path);
  };
  const openVoiceTopup = () => goTo("/app/settings/account#recargar-voz");
  const openChatTopup = () => goTo("/app/settings/account#recargar");
  const openPlans = () => goTo("/app/settings/account#plan");

  const planChange = !!plan && canChangePlan && allowPlanChange;
  const planAdvisor = !!plan && canChangePlan && !allowPlanChange;
  const chatTopup = brand.id !== "etb" && !!plan && !plan.perUse && (onRecargas || canChangePlan);
  const footerTopup = chatTopup ? openChatTopup : vCanTopUp ? openVoiceTopup : null;

  const socialRows: { type: string; label: string; count: number }[] = [];
  if (social?.instagram != null) socialRows.push({ type: "instagram", label: "Instagram", count: social.instagram });
  if (social?.messenger != null) socialRows.push({ type: "messenger", label: "Messenger", count: social.messenger });

  const planTitle = onRecargas ? "Créditos" : plan?.planName ?? null;
  const hasVoiceBlock = !!vWallet || vLegacy;
  const chatBalanceCop = onRecargas ? recargas?.balanceCop ?? null : creditCoverage?.balanceCop ?? null;
  const openDetail = () => setDetailOpen(true);

  const cardActions = (planChange || planAdvisor || onRecargas || vCanTopUp) && (
    <div className="mt-2.5 space-y-1.5" data-testid="usage-card-actions">
      {planAdvisor && <div className="text-[11px] leading-snug text-mute">Para cambiar de plan, contacta a tu asesor comercial.</div>}
      {(planChange || onRecargas || vCanTopUp) && (
        <div className="flex gap-1.5">
          {planChange && (
            <Button type="button" size="sm" variant="outline" className="h-8 flex-1 px-2 text-[12px]" onClick={openPlans}>
              Cambiar plan
            </Button>
          )}
          {onRecargas ? (
            <Button type="button" size="sm" className="h-8 flex-1 px-2 text-[12px]" onClick={() => navigate("/app/settings/account#recargar")} disabled={!userCanTopUp} title={topupTitle}>
              Recargar
            </Button>
          ) : vCanTopUp && (
            <Button type="button" size="sm" className="h-8 flex-1 px-2 text-[12px]" onClick={openVoiceTopup} disabled={!userCanTopUp} title={topupTitle}>
              Recargar voz
            </Button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-xl border bg-background px-3 py-2.5" data-testid="usage-card">
      <div className="flex items-center gap-2">
        <span className="flex-1 truncate text-[13px] font-medium text-ink-900">Consumo del mes</span>
        <button type="button" onClick={openDetail} className="shrink-0 text-[11px] font-medium text-flow-violet hover:underline">
          Ver detalle <span aria-hidden="true">›</span>
        </button>
      </div>

      <div className="mt-2 cursor-pointer space-y-1.5 text-[12px]" onClick={openDetail} data-testid="usage-card-body">
        {onRecargas && recargas && (
          <CompactRow
            icon={<Wallet className={cn("h-3.5 w-3.5 shrink-0", recargasCanAnswer(recargas) ? "text-mute" : "text-destructive")} aria-hidden="true" />}
            label={recargasCanAnswer(recargas) ? "Saldo de créditos" : "Saldo agotado"}
            value={formatCop(recargas.balanceCop)}
            valueClassName={recargasCanAnswer(recargas) ? undefined : "text-destructive"}
          />
        )}
        {plan && !onRecargas && (
          <>
            <CompactRow
              icon={<MessageSquare className={ROW_ICON} aria-hidden="true" />}
              label={plan.lifetime ? "Conversaciones totales" : compensation ? "Del plan" : "Conversaciones"}
              value={plan.unlimited ? `${fmt(plan.used)}/∞` : plan.included === null ? fmt(plan.used) : `${fmt(plan.used)}/${fmt(plan.included)}`}
              valueClassName={TONE_TEXT[plan.tone]}
            >
              {plan.included !== null && <MiniBar pct={(plan.used / plan.included) * 100} tone={plan.tone} />}
            </CompactRow>
            {compensation && (
              <CompactRow testId="usage-compensation" icon={<Gift className={ROW_ICON} aria-hidden="true" />} label="Compensatorias" value={fmt(compensation.balance)} />
            )}
            {creditsPay && (
              <CompactRow icon={<Wallet className={ROW_ICON} aria-hidden="true" />} label="Usando saldo de créditos" value={formatCop(creditCoverage?.balanceCop ?? 0)} />
            )}
            {pastIncluded && creditCoverage?.exhausted && (
              <CompactRow
                testId="usage-credits-exhausted"
                icon={<Wallet className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />}
                label="Saldo agotado"
                value={formatCop(creditCoverage.balanceCop ?? 0)}
                valueClassName="text-destructive"
              />
            )}
          </>
        )}
        {metaCost && (
          <div className="space-y-1" data-testid="whatsapp-replies-row">
            <CompactRow
              testId="whatsapp-compact-estimate"
              icon={<ChannelIcon type="whatsapp" className="h-3.5 w-3.5 shrink-0" />}
              label="WhatsApp"
              value={metaCost.billable > 0 ? `~${formatUsd(metaCost.usd)}` : formatUsd(0)}
            />
            <RepliesBar total={metaCost.delivered} allowance={totalAllowance} showMark={singleNumber} />
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[11px] text-mute tabular-nums">
              <span className="truncate">
                {fmt(metaCost.delivered)} respuestas{singleNumber ? "" : ` · ${numberCount} números`}
              </span>
              {overFree > 0 ? (
                <OverChip>
                  +{fmt(overFree)} con costo
                  {!singleNumber && numbersOverFree < numberCount && ` en ${numbersOverFree} ${numbersOverFree === 1 ? "número" : "números"}`}
                </OverChip>
              ) : (
                <span>{fmt(freeLeft)} gratis restantes</span>
              )}
            </div>
          </div>
        )}
        {vWallet && (
          <CompactRow
            testId="voice-wallet-row"
            icon={<Phone className={ROW_ICON} aria-hidden="true" />}
            label="Saldo de voz"
            value={formatCopCredits(vWallet.balanceCop)}
            valueClassName={TONE_TEXT[vWalletTone]}
          />
        )}
        {vWallet && !vWallet.canCall && <div className="text-[11px] text-destructive">Sin saldo de voz.</div>}
        {vLegacy && (
          <CompactRow
            testId="voice-minutes-row"
            icon={<Phone className={ROW_ICON} aria-hidden="true" />}
            label="Minutos de voz"
            value={vHasBalance ? fmtMinutes(vRemaining) : "0 min"}
            valueClassName={TONE_TEXT[vTone]}
          >
            <MiniBar pct={vShare * 100} tone={vTone} />
          </CompactRow>
        )}
        {vLegacy && !vCanTopUp && vRemaining <= 0 && <div className="text-[11px] text-mute">Contacta a un asesor.</div>}
      </div>

      {cardActions}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent size="xl">
          <DialogHeader className="pr-6">
            <DialogTitle>{planTitle ? `Consumo del mes · ${planTitle}` : "Consumo del mes"}</DialogTitle>
            <DialogDescription className="sr-only">Detalle del consumo del mes por plan, voz y canales.</DialogDescription>
          </DialogHeader>

          <DialogBody className="gap-3">
            {(plan || hasVoiceBlock) && (
              <div className="grid gap-3 md:grid-cols-2">
                {onRecargas && recargas && (
                  <DetailBlock
                    testId="usage-detail-plan"
                    title="Plan"
                    className={cn(!hasVoiceBlock && "md:col-span-2")}
                    aside={planChange && <BlockLink onClick={openPlans}>Cambiar plan</BlockLink>}
                  >
                    <div className="space-y-1.5">
                      <DetailRow label="Plan">Créditos</DetailRow>
                      <DetailRow label={recargasCanAnswer(recargas) ? "Saldo de créditos" : "Saldo agotado"} valueClassName={recargasCanAnswer(recargas) ? undefined : "text-destructive"}>
                        {formatCop(recargas.balanceCop)}
                      </DetailRow>
                      <DetailRow label="Precio por conversación">{recargas.priceCop === null ? "Por definir" : formatCop(recargas.priceCop)}</DetailRow>
                      {recargas.extrasLeft > 0 && <DetailRow label="Adicionales disponibles">{fmt(recargas.extrasLeft)}</DetailRow>}
                    </div>
                    <BlockLink onClick={openChatTopup} disabled={!userCanTopUp} title={topupTitle}>Recargar</BlockLink>
                  </DetailBlock>
                )}
                {plan && !onRecargas && (
                  <DetailBlock
                    testId="usage-detail-plan"
                    title="Plan"
                    className={cn(!hasVoiceBlock && "md:col-span-2")}
                    aside={planChange && <BlockLink onClick={openPlans}>Cambiar plan</BlockLink>}
                  >
                    <div className="space-y-1.5">
                      <DetailRow label="Plan">{plan.planName.replace(/^plan\s+/i, "")}</DetailRow>
                      <DetailRow label={plan.lifetime ? "Periodo" : "Ciclo"} valueClassName={plan.lifetime ? "font-normal" : undefined}>
                        {plan.lifetime ? (
                          <>
                            <span className="block">Desde que se creó la cuenta</span>
                            <span className="block text-[11px] leading-tight text-mute">No se reinicia cada mes</span>
                          </>
                        ) : plan.periodStart && plan.periodEnd ? (
                          `${fmtDay(plan.periodStart)} → ${fmtDay(plan.periodEnd)}`
                        ) : (
                          "Sin ciclo activo"
                        )}
                      </DetailRow>
                      {plan.quotaRenewsOn && <DetailRow label="Cupo del mes">Se renueva el {fmtDay(plan.quotaRenewsOn)}</DetailRow>}
                      <DetailRow label="Conversaciones usadas" valueClassName={TONE_TEXT[plan.tone]}>
                        {plan.perUse
                          ? `${fmt(plan.used)} · por uso`
                          : plan.included === null
                            ? `${fmt(plan.used)}${overview?.limit.unlimited ? " · ilimitadas" : ""}`
                            : `${fmt(plan.used)} de ${fmt(plan.included)}`}
                      </DetailRow>
                      {plan.included !== null && (
                        plan.used > plan.included && creditsPay ? (
                          <DetailRow label="Pagadas con saldo">{fmt(plan.used - plan.included)}</DetailRow>
                        ) : plan.used > plan.included ? (
                          <DetailRow label="Excedentes" valueClassName="text-destructive">{fmt(plan.used - plan.included)}</DetailRow>
                        ) : (
                          <DetailRow label="Restantes">{fmt(plan.included - plan.used)}</DetailRow>
                        )
                      )}
                      {chatBalanceCop !== null && (
                        <DetailRow label={creditCoverage?.exhausted ? "Saldo agotado" : "Saldo de chat"} valueClassName={creditCoverage?.exhausted ? "text-destructive" : undefined}>
                          {formatCop(chatBalanceCop)}
                        </DetailRow>
                      )}
                    </div>
                    {compensation && (
                      <div className="space-y-1.5 border-t pt-2" data-testid="usage-compensation-detail">
                        <div className="font-medium text-ink-900">Conversaciones compensatorias</div>
                        <DetailRow label="Saldo disponible">{fmt(compensation.balance)}</DetailRow>
                        <DetailRow label="Consumidas">{fmt(compensation.used)}</DetailRow>
                        <div className="text-[12px] leading-snug text-mute">Se usan al agotar el plan y no cuentan en su consumo.</div>
                        {canChangePlan && <BlockLink onClick={() => goTo("/app/settings/account")}>Ver detalle</BlockLink>}
                      </div>
                    )}
                    {planAdvisor && <div className="text-[12px] leading-snug text-mute">Para cambiar de plan, contacta a tu asesor comercial.</div>}
                  </DetailBlock>
                )}
                {vWallet && (
                  <DetailBlock
                    testId="voice-wallet-detail"
                    title="Voz"
                    className={cn(!plan && "md:col-span-2")}
                    aside={vCanTopUp && <BlockLink onClick={openVoiceTopup} disabled={!userCanTopUp} title={topupTitle}>Recargar</BlockLink>}
                  >
                    <div className="space-y-1.5">
                      <DetailRow label="Saldo de voz" valueClassName={TONE_TEXT[vWalletTone]}>{formatCopCredits(vWallet.balanceCop)}</DetailRow>
                      {vWalletMinutes !== null && <DetailRow label="Equivale a">≈ {vWalletMinutes.toLocaleString("es-CO")} min</DetailRow>}
                      {vWallet.priceCop !== null && <DetailRow label="Precio por minuto">{formatCopCredits(vWallet.priceCop)}</DetailRow>}
                      {vWallet.legacyMinutesLeft > 0 && <DetailRow label="Minutos cargados">{fmtMinutes(vWallet.legacyMinutesLeft)}</DetailRow>}
                    </div>
                    {!vWallet.canCall && <div className="text-[12px] text-destructive">Sin saldo de voz: recarga para volver a llamar.</div>}
                  </DetailBlock>
                )}
                {vLegacy && voice && (
                  <DetailBlock
                    testId="voice-minutes-detail"
                    title="Voz"
                    className={cn(!plan && "md:col-span-2")}
                    aside={vCanTopUp && <BlockLink onClick={openVoiceTopup} disabled={!userCanTopUp} title={topupTitle}>Recargar</BlockLink>}
                  >
                    {vHasBalance ? (
                      <div className="space-y-1.5">
                        <DetailRow label="Recargados">{fmtMinutes(vLoaded)}</DetailRow>
                        <DetailRow label="Usados">{fmtMinutes(voice.used)}</DetailRow>
                        <DetailRow label="Restantes" valueClassName={TONE_TEXT[vTone]}>{fmtMinutes(vRemaining)}</DetailRow>
                        {vRemaining <= 0 && !vCanTopUp && <div className="text-[12px] text-destructive">Sin minutos: contacta a un asesor para recargar.</div>}
                      </div>
                    ) : (
                      <div className="text-[12px] text-mute">Aún no tienes minutos cargados. Contacta a un asesor para recargar.</div>
                    )}
                  </DetailBlock>
                )}
              </div>
            )}

            {metaCost && (
              <DetailBlock testId="usage-detail-whatsapp" title="WhatsApp" aside={<span className="text-[12px] text-mute">Lo cobra Meta, no {platformName}</span>}>
                <div className="grid items-start gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <WhatsAppEstimate cost={metaCost} />
                  <div className="min-w-0 space-y-2">
                    {metaCost.perNumber.map((r) => {
                      const over = Math.max(0, r.delivered - r.allowance);
                      return (
                        <div key={r.channelId} className="space-y-1.5 rounded-lg border px-3 py-2 text-[12px] tabular-nums" data-testid="whatsapp-number-card">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-semibold text-ink-900">{r.phone ?? r.channelName}</span>
                            <span className="shrink-0 font-medium text-ink-900">{r.billable > 0 ? `~${formatUsd(r.usd)}` : formatUsd(0)}</span>
                          </div>
                          <RepliesBar total={r.delivered} allowance={r.allowance} />
                          <div className="flex items-center justify-between gap-2 text-mute">
                            <span className="truncate">{fmt(r.delivered)} resp. · ~{fmt(r.projected)} al cierre</span>
                            {over > 0 ? <OverChip>+{fmt(over)} con costo</OverChip> : <span className="shrink-0">{fmt(r.allowance - r.delivered)} gratis</span>}
                          </div>
                          {showSent && (
                            <div className="space-y-0.5 border-t pt-1 text-mute" data-testid="whatsapp-sent-detail">
                              <div className="flex justify-between"><span>Total enviados</span><span>{fmt(r.sentTotal)}</span></div>
                              <div className="flex justify-between"><span>Plantillas</span><span>{fmt(r.sentTemplates)}</span></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div className="text-[12px] leading-snug text-mute" data-testid="meta-cost-note">
                      Solo WhatsApp tiene costo: Meta da 1.000 respuestas gratis al mes por número y cobra desde la 1.001. Es un cobro de
                      Meta, no de {platformName}. Sin método de pago en Meta, desde la 1.001 tus respuestas no llegan. <PaymentHelpLink />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSent((v) => !v)}
                      aria-expanded={showSent}
                      className="text-[12px] font-medium text-flow-violet hover:underline"
                    >
                      {showSent ? "Ocultar enviados y plantillas" : "Ver enviados y plantillas"}
                    </button>
                  </div>
                </div>
              </DetailBlock>
            )}

            {socialRows.length > 0 && (
              <DetailBlock testId="usage-detail-social" title="Otros canales" aside={<span className="text-[12px] text-mute">sin costo</span>}>
                <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                  {socialRows.map((r) => (
                    <div key={r.type} className="flex items-center gap-2" data-testid={`social-row-${r.type}`}>
                      <ChannelIcon type={r.type} className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-mute">{r.label}</span>
                      <span className="font-semibold tabular-nums text-ink-900">{fmt(r.count)}</span>
                    </div>
                  ))}
                </div>
                <div className="text-[12px] text-mute">Mensajes enviados en el mes. Instagram y Messenger no tienen costo de Meta.</div>
              </DetailBlock>
            )}
          </DialogBody>

          {(planChange || footerTopup) && (
            <DialogFooter className="gap-2 border-t pt-4">
              {planChange && (
                <Button type="button" variant="outline" onClick={openPlans}>
                  Cambiar plan
                </Button>
              )}
              {footerTopup && (
                <Button type="button" onClick={footerTopup} disabled={!userCanTopUp} title={topupTitle}>
                  Recargar saldo
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
